import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import documentsAreaMessages from '../../messages/documents-area/documents-area.messages';
import {
  CommunicationBenefitsType,
  communicationBenefitsTypes,
  CommunicationDividendsType,
  communicationDividendsTypes,
  CommunicationDocumentsStatus,
  CommunicationOperationsType,
  communicationOperationsTypes,
  CommunicationOtherLettersType,
  communicationOtherLettersTypes,
  communicationReportsTypes,
  CommunicationsFilter,
  RangeDate,
  TabItem,
} from '../../model';
import {
  communicationsDocumentsActions,
  documentsAreaCommonSliceActions,
  downloadCommunicationDocuments,
  getCommunicationFilter,
  labelForCommunicationsDocumentsType,
} from '../../store/features/documents';
import { isRangeDateValid } from '../../utils/validators';
import { useAppDispatch, useAppSelector } from '../redux';

const reportKey: CommunicationOtherLettersType = 'RENDICONTO_CDL';

const getSanitizedFilter = (filter: CommunicationsFilter): CommunicationsFilter => {
    if (!filter) return null;
    const { rangeDate } = filter;
    if (!rangeDate) return filter;

    const { from, to } = rangeDate;
    return {
        ...filter,
        rangeDate: {
            from: from ? new Date(from) : undefined,
            to: to ? new Date(to) : undefined,
        }
    }
}

export interface CommunicationDocumentsFilterLogic {
  filter: CommunicationsFilter;
  operations: TabItem<CommunicationOperationsType>[];
  status: TabItem<CommunicationDocumentsStatus>[];
  dividends: TabItem<CommunicationDividendsType>[];
  benefits: TabItem<CommunicationBenefitsType>[];
  other: TabItem<CommunicationOtherLettersType>[];
  isDateRangeValid: (range: RangeDate) => boolean;
  handleDateChange: (range: RangeDate) => void;
  handleCategoryOperationsChange: (
    value: (CommunicationOperationsType)[]
  ) => void;
  handleCategoryDividendsChange: (value: CommunicationDividendsType[]) => void;
  handleCategoryBenefitsChange: (value: CommunicationBenefitsType[]) => void;
  handleCategoryOtherLettersChange: (
    value: CommunicationOtherLettersType[]
  ) => void;
  handleStatusChange: (value: CommunicationDocumentsStatus[]) => void;
  handleSelectAll: (key: keyof Omit<CommunicationsFilter, 'rangeDate'>) => void;

  handleSubmitFilter: () => void;
  handleResetFilter: () => void;
  handleBackToDocumentPage: () => void; //this is only for the mobile
}

export const useCommunicationDocumentsFilterLogic =
  (): CommunicationDocumentsFilterLogic => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const filterFromRedux:any = useAppSelector(getCommunicationFilter);
    const [localFilter, setLocalFilter] = useState<CommunicationsFilter>(() => getSanitizedFilter(filterFromRedux));

    useEffect(() => {
      setLocalFilter(getSanitizedFilter(filterFromRedux));
    }, [filterFromRedux]);

    const handleDateChange = useCallback(
      (rangeDate: RangeDate) => {
        const clone: CommunicationsFilter = JSON.parse(
          JSON.stringify(localFilter)
        ) || {};
        setLocalFilter({ ...clone, rangeDate });
      },
      [localFilter]
    );
    const isDateRangeValid = useCallback((rangeDate: RangeDate) => {
      return isRangeDateValid(rangeDate, true);
    }, []);

    const handleStatusChange = useCallback(
      (value: CommunicationDocumentsStatus[]) => {
        const clone: CommunicationsFilter = JSON.parse(
          JSON.stringify(localFilter)
        ) || {};
        setLocalFilter({ ...clone, status: value });
      },
      [localFilter]
    );

    const handleCategoryOperationsChange = useCallback(
      (value: CommunicationOperationsType[]) => {
        const clone: CommunicationsFilter = JSON.parse(
          JSON.stringify(localFilter)
        ) || {};
        setLocalFilter({ ...clone, operations: value });
      },
      [localFilter]
    );
    const handleCategoryDividendsChange = useCallback(
      (value: CommunicationDividendsType[]) => {
        const clone: CommunicationsFilter = JSON.parse(
          JSON.stringify(localFilter)
        ) || {};
        setLocalFilter({ ...clone, dividends: value });
      },
      [localFilter]
    );
    const handleCategoryBenefitsChange = useCallback(
      (value: CommunicationBenefitsType[]) => {
        const clone: CommunicationsFilter = JSON.parse(
          JSON.stringify(localFilter)
        ) || {};
        setLocalFilter({ ...clone, benefits: value });
      },
      [localFilter]
    );
    const handleCategoryOtherLettersChange = useCallback(
      (value: CommunicationOtherLettersType[]) => {
        const clone: CommunicationsFilter = JSON.parse(
          JSON.stringify(localFilter)
        ) || {};
        setLocalFilter({ ...clone, other: value });
      },
      [localFilter]
    );

    const handleSubmitFilter = useCallback(() => {
      const hasAnySelection = !!(
        localFilter?.status?.length ||
        localFilter?.operations?.length ||
        localFilter?.dividends?.length ||
        localFilter?.benefits?.length ||
        localFilter?.other?.length
      );
      if (hasAnySelection) {
        const activeFilter: CommunicationsFilter = {
          ...({ status: localFilter.status }),
          ...(localFilter?.operations?.length
            ? { operations: localFilter.operations }
            : { operations: [] }),
          ...(localFilter?.dividends?.length
            ? { dividends: localFilter.dividends }
            : { dividends: [] }),
          ...(localFilter?.benefits?.length
            ? { benefits: localFilter.benefits }
            : { benefits: [] }),
          ...(localFilter?.other?.length
            ? { other: localFilter.other }
            : { other: [] }),
          ...({ rangeDate: localFilter.rangeDate }),
        };

        dispatch(communicationsDocumentsActions.setFilter(activeFilter));
      } else {
        dispatch(communicationsDocumentsActions.setFilter(localFilter));
      }
      dispatch(downloadCommunicationDocuments());
    }, [dispatch, localFilter]);

    const handleResetFilter = useCallback(() => {
      setLocalFilter(null);
      dispatch(communicationsDocumentsActions.resetFilter());
      dispatch(downloadCommunicationDocuments());
    }, [dispatch, setLocalFilter]);

    const handleSelectAll = useCallback(
      (key: keyof Omit<CommunicationsFilter, 'rangeDate'>) => {
        const clone: CommunicationsFilter =
          JSON.parse(JSON.stringify(localFilter)) || {};
        const allOptions = {
          status: ['Read', 'NotRead'],
          operations: [...communicationOperationsTypes],
          dividends: [...communicationDividendsTypes],
          benefits: communicationBenefitsTypes.filter(
            (item) =>
              item !== 'LETTERA_BENVENUTO_MONO_NGA_SMART' &&
              item !== 'LETTERA_BENVENUTO_MONO_NGA_FULL' &&
              item !== 'LETTERA_BENVENUTO_MONO_MAX'
          ),
          other: [
            ...communicationOtherLettersTypes.filter(
              (item) => !communicationReportsTypes.includes(item as any) && item !== 'LETTERA_ADV_SCADUTA_CLIENTI_CF_TECNICI'
            ),
            reportKey,
          ],
        };
        const currentSelection = clone[key] || [];
        const allSelected = currentSelection.length === allOptions[key].length;
        clone[key] = allSelected ? [] : allOptions[key] as any;
        setLocalFilter({ ...clone });
      },
      [setLocalFilter, localFilter]
    );

    const status: TabItem<CommunicationDocumentsStatus>[] = [
      {
        type: 'Read',
        label: formatMessage(documentsAreaMessages.read),
      },
      {
        type: 'NotRead',
        label: formatMessage(documentsAreaMessages.notRead),
      },
    ];
    const operations: TabItem<CommunicationOperationsType>[] = useMemo(
      () =>
        communicationOperationsTypes.map(
          (item): TabItem<CommunicationOperationsType> => ({
            type: item,
            label: formatMessage(labelForCommunicationsDocumentsType(item)),
          })
        ),
      [formatMessage]
    );

    const dividends: TabItem<CommunicationDividendsType>[] =
      communicationDividendsTypes.map((item) => ({
        type: item,
        label: formatMessage(labelForCommunicationsDocumentsType(item)),
      }));

    const benefits: TabItem<CommunicationBenefitsType>[] =
      communicationBenefitsTypes
        .filter(
          (item) =>
            item !== 'LETTERA_BENVENUTO_MONO_NGA_SMART' &&
            item !== 'LETTERA_BENVENUTO_MONO_NGA_FULL' &&
            item !== 'LETTERA_BENVENUTO_MONO_MAX'
        )
        .map((item) => ({
          type: item,
          label: formatMessage(labelForCommunicationsDocumentsType(item)),
        }));

    const other: TabItem<CommunicationOtherLettersType>[] =
      communicationOtherLettersTypes
        .filter((item) => !communicationReportsTypes.includes(item as any) && item !== 'LETTERA_ADV_SCADUTA_CLIENTI_CF_TECNICI')
        .map((item) => ({
          type: item,
          label: formatMessage(labelForCommunicationsDocumentsType(item)),
        }));

    other.push({
      type: reportKey,
      label: formatMessage(labelForCommunicationsDocumentsType(reportKey)),
    });

    const handleBackToDocumentPage = useCallback(() => {
      dispatch(documentsAreaCommonSliceActions.setNavigationMustReset(false));
    }, [dispatch]);

    return {
      filter: localFilter,
      operations,
      status,
      benefits,
      dividends,
      other,
      handleStatusChange,
      handleSubmitFilter,
      handleResetFilter,
      handleCategoryOperationsChange,
      handleCategoryDividendsChange,
      handleCategoryBenefitsChange,
      handleCategoryOtherLettersChange,
      handleDateChange,
      isDateRangeValid,
      handleSelectAll,
      handleBackToDocumentPage,
    };
  };
